// Lib
import { createSelector } from 'reselect';
import { compact } from 'lodash';

// Util
import { getBoardIdeaContent, getLimitedIdeaContent } from '../../../../../../common/ai/aiBoardConversionUtils';
import { AiConversationMessage } from '../../../../../../common/ai/aiConversationTypes';
import { PromptShortcut } from '../../../../../../common/ai/aiPromptShortcutTypes';
import { convertIdeasToStructuredText } from '../../../../../../common/ai/aiIdeaConversionUtils';

// Selector
import { getCurrentBoard, getCurrentBoardVisibleDescendants } from '../../../../../element/selectors/currentBoardSelector';
import { getCurrentBoardCommentsSelector } from '../../../../../element/comment/store/commentsSelector';

// Constants
import { PROMPT_MAX_TOKEN_LENGTH } from './state/aiToolStateConstants';

const currentBoardContentSelector = createSelector(
    getCurrentBoard,
    getCurrentBoardVisibleDescendants,
    getCurrentBoardCommentsSelector,
    (currentBoard, visibleDescendants, visibleDescendantComments) => {
        const content = getBoardIdeaContent(
            currentBoard.toJS(),
            visibleDescendants.toArray().map((element) => element.toJS()),
            visibleDescendantComments.toArray().map((comment) => comment.toJS()),
        );

        const limitedContent = getLimitedIdeaContent(content, PROMPT_MAX_TOKEN_LENGTH);

        return convertIdeasToStructuredText(limitedContent);
    },
);

const getCurrentBoardContentPrompt =
    () =>
    (dispatch: Function, getState: Function): string => {
        const boardContent = currentBoardContentSelector(getState());

        return `\n###\n${boardContent}`;
    };

type GetPromptMethod = () => (dispatch: Function, getState: Function) => string;
const PROMPT_SHORTCUT_METHODS: { [key: string]: GetPromptMethod } = {
    getCurrentBoardContentPrompt,
};

export const getPromptMethod = (methodName: string): GetPromptMethod | undefined => PROMPT_SHORTCUT_METHODS[methodName];

export const constructMessagesFromPromptShortcuts = (
    chosenPromptShortcuts: PromptShortcut[],
    dispatchToReduxStore: Function,
): AiConversationMessage[] =>
    chosenPromptShortcuts
        .filter(({ isRoot }) => !isRoot)
        .flatMap(({ promptText, assistantPromptText, getPromptMethod }): AiConversationMessage[] => {
            // if there is an assistant prompt, then we need to split the prompt into two messages
            // one that is the user-facing prompt message, and one that is visible to the assistant

            if (getPromptMethod) {
                return compact([
                    {
                        role: 'user',
                        content: promptText || '',
                        visibility: {
                            user: true,
                            assistant: false,
                        },
                    },
                    assistantPromptText && {
                        role: 'user',
                        content: assistantPromptText,
                        visibility: {
                            user: false,
                            assistant: true,
                        },
                    },
                    getPromptMethod && {
                        role: 'user',
                        content: dispatchToReduxStore(getPromptMethod()) || '',
                        visibility: {
                            user: false,
                            assistant: true,
                        },
                    },
                ]);
            }

            return [
                {
                    role: 'user',
                    content: assistantPromptText || promptText || '',
                    visibility: {
                        user: true,
                        assistant: true,
                    },
                },
            ];
        });

export const combineAssistantPromptMessage = (messages: AiConversationMessage[]): AiConversationMessage[] => {
    // if there is only one message, then there is nothing to combine
    if (messages.length === 1) return messages;

    const userMessages = messages.filter((message) => message.visibility.user);
    const assistantMessages = messages.filter((message) => message.visibility.assistant);

    const combinedAssistantPrompt = assistantMessages.map((message) => message.content).join(' ');

    // if assistant-visible messages contain any messages that aren't user-visible, then we need to return two messages
    // one that is the user-facing prompt message, and one that is visible to the assistant
    if (assistantMessages.find((message) => !message.visibility.user)) {
        return [
            ...userMessages,
            {
                role: 'user',
                content: combinedAssistantPrompt,
                visibility: {
                    user: false,
                    assistant: true,
                },
            },
        ];
    }

    // otherwise, return the combined assistant prompt as a single message,
    // visible to both the user and the assistant
    return [
        {
            role: 'user',
            content: combinedAssistantPrompt,
            visibility: {
                user: true,
                assistant: true,
            },
        },
    ];
};

export const createUserPromptMessage = (
    promptText: string,
    visibility: { user: boolean; assistant: boolean } = { user: true, assistant: true },
): AiConversationMessage => ({
    role: 'user',
    content: promptText,
    visibility: visibility,
});
